import React from "react";
import { Carousel, Container, Row, Col, Button } from "react-bootstrap";
import { Link } from "react-router-dom";

// Definir tipos para los slides
interface HeroSlide {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  backgroundImage: string;
  buttonText: string;
  buttonLink: string;
  buttonVariant: string;
}

// Datos de los slides
const heroSlides: HeroSlide[] = [
  {
    id: "main",
    title: "MICERVESA",
    subtitle: "Ingredientes Premium",
    description: "Somos distribuidores en mayoreo de las mejores marcas de insumos para micro cervecerías y tiendas de homebrewing en México.",
    backgroundImage: "url(/images/home/Bitmap.jpg)",
    buttonText: "Explorar Productos",
    buttonLink: "/productos",
    buttonVariant: "primary"
  },
  {
    id: "maltas",
    title: "MALTAS PREMIUM",
    subtitle: "Calidad Internacional",
    description: "Descubre nuestra selección de maltas importadas de las mejores malterías del mundo para crear cervezas excepcionales.",
    backgroundImage: "url(/images/home/cat_1.jpg)",
    buttonText: "Ver Maltas",
    buttonLink: "/productos/Maltas",
    buttonVariant: "warning"
  },
  {
    id: "lupulos",
    title: "LÚPULOS AROMÁTICOS",
    subtitle: "Sabores Únicos",
    description: "Explora nuestra colección de lúpulos frescos que aportarán aromas y sabores extraordinarios a tu cerveza artesanal.",
    backgroundImage: "url(/images/home/cat_2.jpg)",
    buttonText: "Descubrir Lúpulos",
    buttonLink: "/productos/Lupulos",
    buttonVariant: "success"
  }
];

const Hero: React.FC = () => {
  return (
    <section className="hero-section">
      <Carousel 
        fade 
        interval={5000}
        pause="hover"
        indicators={true}
        controls={true}
        className="hero-carousel"
      >
        {heroSlides.map((slide, index) => (
          <Carousel.Item key={slide.id}>
            <div 
              className="hero-slide d-flex align-items-center"
              style={{
                minHeight: "50vh",
                backgroundImage: slide.backgroundImage,
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",
                position: "relative"
              }}
            >
              {/* Overlay con gradiente */}
              <div 
                className="position-absolute top-0 start-0 w-100 h-100"
                style={{
                  background: "linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.4) 100%)"
                }}
              />
              
              {/* Contenido del slide */}
              <Container className="position-relative z-1">
                <Row className="justify-content-start">
                  <Col lg={6} md={8} className="text-start text-white">
                    <div className="hero-content">
                      {/* Badge */}
                      <div className="mb-3">
                        <span 
                          className="badge px-3 py-2 fs-6 fw-semibold"
                          style={{
                            backgroundColor: 'rgba(255,255,255,0.2)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.3)'
                          }}
                        >
                          {slide.subtitle}
                        </span>
                      </div>
                      
                      {/* Título principal */}
                      <h1 
                        className="display-4 fw-bold mb-3"
                        style={{
                          textShadow: '2px 2px 4px rgba(0,0,0,0.5)',
                          lineHeight: '1.1'
                        }}
                      >
                        {slide.title}
                      </h1>
                      
                      {/* Descripción */}
                      <p 
                        className="mb-3"
                        style={{
                          maxWidth: '500px',
                          textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                          fontSize: '1.1rem'
                        }}
                      >
                        {slide.description}
                      </p>
                      
                      {/* Botón CTA */}
                      <div className="d-flex flex-column flex-sm-row gap-2 justify-content-start align-items-start">
                        <Link to={slide.buttonLink} className="me-4">
                          <Button 
                            variant={slide.buttonVariant}
                            size="md"
                            className="px-3 py-2 fw-semibold rounded-pill shadow-lg"
                            style={{
                              minWidth: '180px',
                              transition: 'all 0.3s ease',
                              border: 'none'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.3)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
                            }}
                          >
                            {slide.buttonText}
                            <i className="ms-2 me-2 fas fa-arrow-right"></i>
                          </Button>
                        </Link>
                        
                        <Button 
                          variant="outline-light"
                          size="md"
                          className="px-3 py-2 fw-semibold rounded-pill"
                          style={{
                            minWidth: '180px',
                            transition: 'all 0.3s ease',
                            border: '2px solid rgba(255,255,255,0.8)',
                            backgroundColor: 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
                            e.currentTarget.style.transform = 'translateY(-2px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.transform = 'translateY(0)';
                          }}
                        >
                          Conocer Más
                        </Button>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Container>
            </div>
          </Carousel.Item>
        ))}
      </Carousel>
      
      {/* Indicadores personalizados */}
      <style jsx>{`
        .hero-carousel .carousel-indicators {
          bottom: 30px;
          margin-bottom: 0;
        }
        
        .hero-carousel .carousel-indicators button {
          width: 12px;
          height: 12px;
          border-radius: 50%;
          margin: 0 8px;
          border: 2px solid rgba(255,255,255,0.8);
          background-color: transparent;
          transition: all 0.3s ease;
        }
        
        .hero-carousel .carousel-indicators button.active {
          background-color: rgba(255,255,255,0.9);
          transform: scale(1.2);
        }
        
        .hero-carousel .carousel-control-prev,
        .hero-carousel .carousel-control-next {
          width: 60px;
          height: 60px;
          background: rgba(255,255,255,0.1);
          border-radius: 50%;
          top: 50%;
          transform: translateY(-50%);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255,255,255,0.2);
          transition: all 0.3s ease;
        }
        
        .hero-carousel .carousel-control-prev:hover,
        .hero-carousel .carousel-control-next:hover {
          background: rgba(255,255,255,0.2);
          transform: translateY(-50%) scale(1.1);
        }
        
        .hero-carousel .carousel-control-prev {
          left: 30px;
        }
        
        .hero-carousel .carousel-control-next {
          right: 30px;
        }
        
        .hero-content {
          animation: fadeInUp 0.8s ease-out;
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @media (max-width: 768px) {
          .hero-carousel .carousel-control-prev,
          .hero-carousel .carousel-control-next {
            width: 50px;
            height: 50px;
          }
          
          .hero-carousel .carousel-control-prev {
            left: 15px;
          }
          
          .hero-carousel .carousel-control-next {
            right: 15px;
          }
        }
      `}</style>
    </section>
  );
};

export default Hero;
